import React, { useEffect, useState } from 'react';
import { deletedItems } from '../lib/api';
import { Trash2, RotateCcw, AlertCircle, Package, ShoppingCart, Coffee, Layers } from 'lucide-react';
import { format } from 'date-fns';

const RecentlyDeleted = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState('all');

  useEffect(() => {
    fetchDeletedItems();
  }, []);

  const fetchDeletedItems = async () => {
    try {
      const response = await deletedItems.getAll();
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch deleted items:', error);
      if (error.response?.status === 403) {
        alert('Only admins can access this page');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (item) => {
    if (!confirm(`Restore this ${item.module} item?\n\nItem ID: ${item.item_id}\nDeleted: ${format(new Date(item.deleted_at), 'MMM dd, yyyy HH:mm')}`)) {
      return;
    }

    try {
      await deletedItems.restore(item.id);
      alert('Item restored successfully!');
      fetchDeletedItems();
    } catch (error) {
      console.error('Failed to restore item:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to restore item';
      alert(errorMsg);
    }
  };

  const handleCleanup = async () => {
    if (!confirm('Permanently delete all items older than 30 days?\n\nThis action cannot be undone!')) {
      return;
    }

    try {
      const response = await deletedItems.cleanup();
      alert(response.data.message);
      fetchDeletedItems();
    } catch (error) {
      console.error('Failed to cleanup:', error);
      alert('Failed to cleanup old items');
    }
  };

  const getModuleIcon = (module) => {
    const icons = {
      orders: ShoppingCart,
      products: Package,
      green_inventory: Coffee,
      roasted_stock: Coffee,
      packaging: Layers,
      customers: ShoppingCart,
      production_assembly: Layers,
      wastage: Trash2
    };
    const Icon = icons[module] || Package;
    return <Icon className="w-5 h-5" />;
  };

  const getModuleLabel = (module) => {
    const labels = {
      orders: 'Order',
      products: 'Product',
      green_inventory: 'Green Inventory',
      roasted_stock: 'Roasted Stock',
      packaging: 'Packaging',
      customers: 'Customer',
      production_assembly: 'Production Assembly',
      wastage: 'Wastage'
    };
    return labels[module] || module;
  };

  const getModuleBadgeColor = (module) => {
    const colors = {
      orders: 'bg-blue-100 text-blue-700',
      products: 'bg-amber-100 text-amber-700',
      green_inventory: 'bg-green-100 text-green-700',
      roasted_stock: 'bg-orange-100 text-orange-700',
      packaging: 'bg-purple-100 text-purple-700',
      customers: 'bg-pink-100 text-pink-700',
      production_assembly: 'bg-indigo-100 text-indigo-700',
      wastage: 'bg-red-100 text-red-700'
    };
    return colors[module] || 'bg-gray-100 text-gray-700';
  };

  // Get unique modules for filtering
  const modules = ['all', ...new Set(items.map(item => item.module))];
  
  // Filter items by selected module
  const filteredItems = selectedModule === 'all' 
    ? items 
    : items.filter(item => item.module === selectedModule);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recently-deleted-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Recently Deleted Items</h1>
          <p className="text-gray-600 mt-1">Restore items deleted within the last 30 days</p>
        </div>
        {items.length > 0 && (
          <button
            onClick={handleCleanup}
            data-testid="cleanup-old-items-button"
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center space-x-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>Cleanup Old Items (30+ days)</span>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Total Deleted</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{items.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Orders</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {items.filter(i => i.module === 'orders').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Products</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">
            {items.filter(i => i.module === 'products').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Other Items</p>
          <p className="text-2xl font-bold text-gray-600 mt-1">
            {items.filter(i => !['orders', 'products'].includes(i.module)).length}
          </p>
        </div>
      </div>

      {/* Module Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-2">
          {modules.map(module => (
            <button
              key={module}
              onClick={() => setSelectedModule(module)}
              data-testid={`recently-deleted-filter-${module}`}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                selectedModule === module
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {module === 'all' ? 'All Items' : getModuleLabel(module)}
              {module === 'all' 
                ? ` (${items.length})`
                : ` (${items.filter(i => i.module === module).length})`
              }
            </button>
          ))}
        </div>
      </div>

      {/* Deleted Items Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {filteredItems.length === 0 ? (
          <div className="py-12 text-center">
            <div className="flex flex-col items-center justify-center text-gray-400">
              <Trash2 className="w-12 h-12 mb-3" />
              <p className="text-lg font-medium">No deleted items found</p>
              <p className="text-sm">
                {selectedModule === 'all' 
                  ? 'All deleted items are automatically removed after 30 days'
                  : `No deleted ${getModuleLabel(selectedModule).toLowerCase()} items`
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-red-50 to-orange-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Module</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Item ID</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Deleted At</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Deleted By</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Days Ago</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map((item) => {
                  const deletedDate = new Date(item.deleted_at);
                  const daysAgo = Math.floor((new Date() - deletedDate) / (1000 * 60 * 60 * 24));
                  const isExpiringSoon = daysAgo >= 25;

                  return (
                    <tr key={item.id} data-testid={`deleted-row-${item.id}`} className="hover:bg-gray-50 transition">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          <div className={`p-2 rounded-lg ${getModuleBadgeColor(item.module).replace('text-', 'bg-').replace('100', '50')}`}>
                            {getModuleIcon(item.module)}
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getModuleBadgeColor(item.module)}`}>
                            {getModuleLabel(item.module)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-mono text-sm text-gray-700">{item.item_id}</span>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {format(deletedDate, 'MMM dd, yyyy')}
                        <br />
                        <span className="text-xs text-gray-400">{format(deletedDate, 'HH:mm:ss')}</span>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item.deleted_by_name}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`text-sm font-semibold ${
                          isExpiringSoon ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {daysAgo} {daysAgo === 1 ? 'day' : 'days'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        {isExpiringSoon ? (
                          <div className="flex items-center space-x-1 text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-xs font-semibold">Expiring Soon</span>
                          </div>
                        ) : (
                          <span className="text-xs text-green-600 font-semibold">Can Restore</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => handleRestore(item)}
                            disabled={!item.can_restore}
                            data-testid={`restore-button-${item.id}`}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Restore Item"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span>Restore</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentlyDeleted;
